import { App, Button } from 'antd';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingOverlay from '../../components/Common/LoadingOverlay';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { UserProfileStatusEnum } from '../../enums/UserProfileStatusEnum';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import type AuthenticatedUser from '../../interfaces/AuthenticatedUser';
import type DeviceInfo from '../../interfaces/DeviceInfo';
import { stripPreviewPath } from '../../lib/previewRoutePath';
import { authService } from '../../services/authService';
import { UserService } from '../../services/userApiService';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store/store';
import { generateDeviceInfo } from '../../utils/deviceInfo';
import { isUnauthorizedHttpError } from '../../utils/httpError';
import { hasFileAccessScope } from '../../utils/jwt';
import { isActivateTotpPromoSuppressedPath } from '../../utils/suppressActivateTotpPromo';
import { getActivateTotpRemindDays, isTotpEnrollmentMandatory } from '../../utils/totpPolicy';
import { isTotpPromoEligibleRole } from '../../utils/utils';
import { applyViewerWebviewTokensFromSearch } from '../../utils/viewerWebviewAuth';

/**
 * Options for getUserInfo function
 */
export interface GetUserInfoOptions {
  /** Whether to redirect to home page after fetching user info */
  redirectHome?: boolean;
  /** Whether to save authenticatedUserEmail in store (default: true) */
  saveAuthenticatedUserEmail?: boolean;
  /** Whether to redirect to Quick Share if the user has Quick Share enabled */
  redirectToQuickShareIfNecessary?: boolean;
}

/**
 * AuthContextType defines the shape and available values/actions for any consumer of AuthContext.
 */
interface AuthContextType {
  isAdmin: boolean;
  isLoggedIn: boolean;
  isContributor: boolean;
  isExternal: boolean;
  isAdminOrContributor: boolean;
  isReader: boolean;
  isSuperAdmin: boolean;
  token: string | null;
  authenticatedUser: AuthenticatedUser | null;
  /** Profile hydration vs session; entitlements are fail-closed when Unavailable. */
  userProfileStatus: UserProfileStatusEnum;
  /** @param redirectTo - Login path after logout (default `/login`; use `/login?aiGrant=1` from AIGrant). */
  logout: (redirectTo?: string) => Promise<void>;
  redirectLoggedInUser: () => void;
  getUserInfo: (options?: GetUserInfoOptions | boolean) => Promise<void>;
  deviceInfo: DeviceInfo | null;
  isInFileRoute: (route: FilesTableEnum) => boolean;
}

/**
 * The AuthContext with default values. Consumers will always have correct types,
 * even before AuthProvider loads real data.
 */
export const AuthContext = createContext<AuthContextType>({
  deviceInfo: null,
  isAdmin: false,
  isAdminOrContributor: false,
  isContributor: false,
  isExternal: false,
  isLoggedIn: false,
  isReader: false,
  isSuperAdmin: false,
  getUserInfo: async () => {},
  logout: async () => {},
  redirectLoggedInUser: () => {},
  token: null,
  authenticatedUser: null,
  userProfileStatus: UserProfileStatusEnum.Loading,
  isInFileRoute: () => false,
});

// User service instance (used for fetching info)
const userService = new UserService();
// Cached device info, computed once
const deviceInfo = generateDeviceInfo();

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider wraps your app, provides authentication context, status, and handles authentication logic.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // --- Routing and notifications hooks ---
  const location = useLocation();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  // --- Centralized app store selectors ---
  const setAuthenticatedUser = useAppStore((s) => s.setAuthenticatedUser);
  const setAuthenticatedUserEmail = useAppStore((s) => s.setAuthenticatedUserEmail);
  const setUserProfileStatus = useAppStore((s) => s.setUserProfileStatus);
  const userProfileStatus = useAppStore((s) => s.userProfileStatus);
  const clearAllFileThumbnailSkipUntil = useAppStore((s) => s.clearAllFileThumbnailSkipUntil);
  const { webSocket, authenticatedUser } = useAppStore(
    useShallow((s) => ({ webSocket: s.webSocket, authenticatedUser: s.authenticatedUser })),
  );
  const token = useAppStore((s) => s.token);
  const resetStoreOnLogout = useAppStore((s) => s.resetStoreOnLogout);
  const resetSsoLogin = useAppStore((s) => s.appEvents.ssoLogin.reset);
  const openActivateTotp = useAppStore((s) => s.appEvents.activateTotp.open);
  const closeActivateTotp = useAppStore((s) => s.appEvents.activateTotp.close);
  const activateTotpNeverShowAgain = useAppStore((s) => s.appEvents.activateTotp.neverShowAgain);
  const activateTotpRemindAt = useAppStore((s) => s.appEvents.activateTotp.remindAt);
  const isSsoLogin = useAppStore((s) => s.appEvents.ssoLogin.isSsoLogin);

  // --- Internationalization ---
  const { t } = useTranslation();

  // --- Local loading state ---
  const [isLoadingAuthenticatedUser, setIsLoadingAuthenticatedUser] = useState<boolean>(true);

  // Boot ID pattern to prevent async race conditions
  const bootIdRef = useRef(0);
  const getUserInfoRef = useRef<(options?: GetUserInfoOptions | boolean) => Promise<void>>(
    async () => {},
  );

  /**
   * Logs out the user: clears session from server, resets local state, redirects.
   * Safe even if logout API fails.
   */
  const logout = useCallback(
    async (redirectTo = '/login'): Promise<void> => {
      try {
        await authService.logout();
        resetStoreOnLogout();
        resetSsoLogin();
        sessionStorage.removeItem('aiGrantURL');
        navigate(redirectTo, { replace: true });
      } catch (error) {
        // Always best effort: still clear local state and force navigation
        console.error('Logout error:', error);
        resetStoreOnLogout();
        resetSsoLogin();
        sessionStorage.removeItem('aiGrantURL');
        navigate(redirectTo, { replace: true });
      }
    },
    [navigate, resetStoreOnLogout, resetSsoLogin],
  );

  /**
   * Profile fetch failed but session is still valid: keep the user signed in.
   * Stale = last known profile kept; Unavailable = no profile → fail-closed entitlements.
   */
  const handleProfileFetchFailure = useCallback(() => {
    const existingUser = useAppStore.getState().authenticatedUser;
    const notificationKey = 'user-profile-fetch';
    const isStale = existingUser != null;
    setUserProfileStatus(isStale ? UserProfileStatusEnum.Stale : UserProfileStatusEnum.Unavailable);

    // Sticky + bottom-center: always visible; Retry stays reachable until dismissed.
    // After dismiss, useHeader exposes the same Retry in the user menu when status is stale/unavailable.
    notification.open({
      btn: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.destroy(notificationKey);
            void getUserInfoRef.current();
          }}
        >
          {t('errors.userProfileRetry')}
        </Button>
      ),
      duration: 0,
      key: notificationKey,
      message: t(isStale ? 'errors.userProfileStale' : 'errors.userProfileUnavailable'),
      placement: 'bottom',
      type: isStale ? 'warning' : 'error',
    });
  }, [notification, setUserProfileStatus, t]);

  /**
   * Fetches user info and stores it. Optionally redirects based on that info.
   * Logs out only on 401 (session expired). Other errors keep the session and
   * mark the profile as stale/unavailable.
   *
   * @param options - Configuration options or boolean for backward compatibility
   *   - If boolean: treated as redirectHome (backward compatibility)
   *   - If object: { redirectHome?: boolean, saveAuthenticatedUserEmail?: boolean }
   */
  const getUserInfo = useCallback(
    async (options?: GetUserInfoOptions | boolean): Promise<void> => {
      // Handle backward compatibility: if boolean, convert to options object
      const opts: GetUserInfoOptions =
        typeof options === 'boolean'
          ? { redirectHome: options, saveAuthenticatedUserEmail: true }
          : { redirectHome: false, saveAuthenticatedUserEmail: true, ...options };

      // Read tokens directly from store to avoid stale hook values
      // This ensures we use the latest tokens even if they were just updated
      const currentState = useAppStore.getState();
      const currentToken = authService.getToken(); // This reads from store directly

      // Only sync token to customTokens if we have a token but no customTokens
      // This prevents overwriting tokens that were just set (e.g., during impersonation)
      if (currentToken && !currentState.customTokens?.token) {
        const existingCustomTokens = currentState.customTokens;
        useAppStore.getState().setCustomTokens({
          token: currentToken,
          rToken: existingCustomTokens?.rToken || '',
          tokenExpiry: existingCustomTokens?.tokenExpiry,
          rTokenExpiry: existingCustomTokens?.rTokenExpiry,
        });
      }

      await userService
        .getUserInfo()
        .then(async (user) => {
          setAuthenticatedUser(user);
          setUserProfileStatus(UserProfileStatusEnum.Ready);
          notification.destroy('user-profile-fetch');

          // Only save authenticatedUserEmail if explicitly requested (default: true)
          // This allows preserving the original user's email during impersonation
          if (opts.saveAuthenticatedUserEmail !== false) {
            setAuthenticatedUserEmail(user.email);
          }

          // Wipe thumbnail skip map on login so stale skip from previous 401s does not block previews
          clearAllFileThumbnailSkipUntil?.();

          // File-scoped OTP sessions: skip alerts bootstrap (not on the allow-list → 403 DENIED).
          const scopedToken = authService.getToken();
          if (!(scopedToken && hasFileAccessScope(scopedToken))) {
            void useAppStore.getState().fetchActiveAlerts();
            useAppStore.getState().startAlertsPolling();
          }

          if (opts.redirectToQuickShareIfNecessary && user.isQSAvailable) {
            navigate('/quick-share');
            return;
          }

          // Navigate home if that's requested
          if (opts.redirectHome) {
            // If user has Quick Share as default landing, go there instead of role-based home
            if (user.isQSDefault === true && user.isQSAvailable === true) {
              navigate('/quick-share', { replace: true });
              return;
            }
            switch (user.role) {
              case UserRoleEnum.Reader:
              case UserRoleEnum.External:
                navigate('/files/received', { replace: true });
                break;
              default:
                navigate('/files/personal', { replace: true });
                break;
            }
          }
        })
        .catch((error: unknown) => {
          console.error('AuthContext: getUserInfo failed:', error);
          if (isUnauthorizedHttpError(error)) {
            const fileScoped =
              (error as { data?: { error?: string } })?.data?.error ===
                'file-access-session-expired' || hasFileAccessScope(currentToken);
            notification.warning({
              title: t(fileScoped ? 'errors.fileAccessSessionExpired' : 'errors.sessionExpired'),
            });
            if (fileScoped) {
              // Clear tokens only — no login redirect for OTP viewer sessions.
              void authService.logout();
              return;
            }
            void logout();
            return;
          }
          handleProfileFetchFailure();
        });
    },
    [
      clearAllFileThumbnailSkipUntil,
      handleProfileFetchFailure,
      logout,
      navigate,
      notification,
      setAuthenticatedUser,
      setAuthenticatedUserEmail,
      setUserProfileStatus,
      t,
    ],
  );

  getUserInfoRef.current = getUserInfo;

  /**
   * Checks if the current URL path includes the given FilesTableEnum route segment.
   */
  // List path only: opening `/preview/:id` must not change the context value (re-renders every table cell).
  const listPathname = stripPreviewPath(location.pathname);
  const isInFileRoute = useCallback(
    (route: FilesTableEnum) => {
      return listPathname.includes(route);
    },
    [listPathname],
  );

  /**
   * Checks if the user is logged in.
   */
  const isLoggedIn = useMemo(() => authService.isAuthenticated(), [token, authenticatedUser]);

  /**
   * Redirects the logged-in user to their default home: Quick Share if isQSDefault, else role-based.
   */
  const redirectLoggedInUser = useCallback(() => {
    if (isLoggedIn && authenticatedUser) {
      switch (authenticatedUser.role) {
        case UserRoleEnum.Reader:
        case UserRoleEnum.External:
          navigate(`/files/${FilesTableEnum.Received}`, { replace: true });
          break;
        default:
          navigate(`/files/${FilesTableEnum.Personal}`, { replace: true });
          break;
      }
    }
  }, [isLoggedIn, authenticatedUser, navigate]);

  /**
   * Entitlements from profile: fail-closed when profile could not be loaded.
   * Stale keeps last-known role flags (stale-while-revalidate).
   */
  const entitlementsEnabled =
    userProfileStatus === UserProfileStatusEnum.Ready ||
    userProfileStatus === UserProfileStatusEnum.Stale;

  /**
   * Checks if the user is a super admin.
   */
  const isSuperAdmin = useMemo(
    () => entitlementsEnabled && authenticatedUser?.role === UserRoleEnum.SuperAdmin,
    [authenticatedUser, entitlementsEnabled],
  );

  /**
   * Checks if the user is an admin.
   */
  const isAdmin = useMemo(
    () => entitlementsEnabled && authenticatedUser?.role === UserRoleEnum.Admin,
    [authenticatedUser, entitlementsEnabled],
  );

  /**
   * Checks if the user is a contributor.
   */
  const isContributor = useMemo(
    () => entitlementsEnabled && authenticatedUser?.role === UserRoleEnum.Contributor,
    [authenticatedUser, entitlementsEnabled],
  );

  /**
   * Checks if the user is a reader.
   */
  const isReader = useMemo(
    () => entitlementsEnabled && authenticatedUser?.role === UserRoleEnum.Reader,
    [authenticatedUser, entitlementsEnabled],
  );

  /**
   * Checks if the user is an external user.
   */
  const isExternal = useMemo(
    () => entitlementsEnabled && authenticatedUser?.role === UserRoleEnum.External,
    [authenticatedUser, entitlementsEnabled],
  );

  /**
   * Checks if the user is an admin or a contributor.
   * Includes SuperAdmin as it has all admin privileges.
   */
  const isAdminOrContributor = useMemo(
    () => isAdmin || isSuperAdmin || isContributor,
    [isAdmin, isSuperAdmin, isContributor],
  );

  /**
   * The context value provided to consumers (memoized to avoid unnecessary renders).
   */
  const contextValue = useMemo(() => {
    return {
      authenticatedUser,
      deviceInfo,
      getUserInfo,
      isAdmin,
      isAdminOrContributor,
      isContributor,
      isExternal,
      isInFileRoute,
      isLoggedIn,
      isReader,
      isSuperAdmin,
      logout,
      redirectLoggedInUser,
      token,
      userProfileStatus,
    };
  }, [
    authenticatedUser,
    deviceInfo,
    getUserInfo,
    isAdmin,
    isAdminOrContributor,
    isContributor,
    isExternal,
    isInFileRoute,
    isLoggedIn,
    isReader,
    isSuperAdmin,
    logout,
    redirectLoggedInUser,
    token,
    userProfileStatus,
  ]);

  /**
   * On mount: bootstraps authentication, populates store with user,
   * and sets up critical state. Handles async race conditions.
   */
  useEffect(() => {
    let cancelled = false;
    // Boot ID increments for every invocation to prevent race conditions.
    const myBootId = ++bootIdRef.current;
    const isLogoutRoute = location.pathname === '/logout';

    async function doAuthBoot() {
      try {
        setIsLoadingAuthenticatedUser(true);
        setUserProfileStatus(UserProfileStatusEnum.Loading);

        // If component unmounted or this is a stale effect, abort.
        if (cancelled || bootIdRef.current !== myBootId) return;

        // Ensure Zustand `persist` has merged `localStorage` before reading `customTokens`
        // (avoids a race on cold load / E2E where `initialize()` would otherwise skip `getUserInfo`).
        await useAppStore.persist.rehydrate();

        if (cancelled || bootIdRef.current !== myBootId) return;

        // WebView viewer: seed session from `token`+`rToken` QS (overwrites persisted session)
        // before initialize / any viewer fetch. Strips secrets via replace; no home redirect.
        await applyViewerWebviewTokensFromSearch({
          pathname: location.pathname,
          search: location.search,
          navigate,
          getUserInfo: (options) => getUserInfoRef.current(options),
        });

        if (cancelled || bootIdRef.current !== myBootId) return;

        // /logout (incl. from AIGrant): only need rehydrated rToken for server revoke —
        // skip profile/alerts so we don't load user data into memory just to clear it.
        if (isLogoutRoute) {
          return;
        }

        // Initialize authentication; may set tokens and/or user in store.
        await authService.initialize();

        if (cancelled || bootIdRef.current !== myBootId) return;

        // If authenticated but user not in store, fetch user; otherwise set email
        const currentUser = useAppStore.getState().authenticatedUser;
        const isOnBehalfOfUser = useAppStore.getState().onBehalfOfUser !== null;
        const isAuthedBoot = authService.isAuthenticated();
        if (isAuthedBoot && currentUser == null) {
          try {
            const user = await userService.getUserInfo();
            if (cancelled || bootIdRef.current !== myBootId) return;
            setAuthenticatedUser(user);
            setUserProfileStatus(UserProfileStatusEnum.Ready);
            // Don't update authenticatedUserEmail if on behalf of user to preserve original admin's email
            if (!isOnBehalfOfUser) {
              setAuthenticatedUserEmail(user.email);
            }
          } catch (profileError: unknown) {
            if (isUnauthorizedHttpError(profileError)) {
              throw profileError;
            }
            if (!cancelled && bootIdRef.current === myBootId) {
              console.error('AuthContext: getUserInfo failed during boot:', profileError);
              handleProfileFetchFailure();
            }
          }
        } else if (currentUser != null) {
          setUserProfileStatus(UserProfileStatusEnum.Ready);
          // Don't update authenticatedUserEmail if impersonating to preserve original admin's email
          if (!isOnBehalfOfUser) {
            setAuthenticatedUserEmail(currentUser.email);
          }
        }

        if (!cancelled && bootIdRef.current === myBootId && authService.isAuthenticated()) {
          const bootToken = authService.getToken();
          if (!(bootToken && hasFileAccessScope(bootToken))) {
            void useAppStore.getState().fetchActiveAlerts();
            useAppStore.getState().startAlertsPolling();
          }
        }
      } catch (err: unknown) {
        console.error('AuthContext: Error during boot:', err);
        if (!cancelled && bootIdRef.current === myBootId) {
          if (isUnauthorizedHttpError(err) || !authService.isAuthenticated()) {
            if (isUnauthorizedHttpError(err)) {
              notification.warning({
                title: t('errors.sessionExpired'),
              });
            } else {
              notification.error({
                title:
                  t?.('errors.genericFetchError', { resource: t?.('common.user') }) ?? 'Auth error',
              });
            }
            await logout();
          } else {
            handleProfileFetchFailure();
          }
        }
      } finally {
        if (!cancelled && bootIdRef.current === myBootId) {
          setIsLoadingAuthenticatedUser(false);
        }
      }
    }

    doAuthBoot();

    return () => {
      cancelled = true;
    };
    // Boot once on mount; handlers use store/refs for latest values.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional cold-boot only
  }, []);

  /**
   * Listen for logout event from store's event system. On such event, call logout().
   */
  useEffect(() => {
    const unsubscribeMakeLogout = useAppStore.getState().events.on.makeLogout(() => {
      logout();
    });
    return unsubscribeMakeLogout;
  }, [logout]);

  /**
   * Cross-tab logout: when another tab in the same browser clears the persisted session
   * (i.e. logs out), the `storage` event fires here. We mirror the logout locally so this tab
   * stops operating with a token that the other tab already revoked on the server.
   * This is local-only cleanup (no server call) since the session was already invalidated.
   */
  useEffect(() => {
    const STORE_KEY = 'appStore';
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== STORE_KEY) return;
      // Only react if this tab still believes it is authenticated.
      if (!authService.isAuthenticated()) return;

      let tokensCleared = false;
      try {
        const parsed = event.newValue ? JSON.parse(event.newValue) : null;
        tokensCleared = !parsed?.state?.customTokens;
      } catch {
        tokensCleared = false;
      }

      if (tokensCleared) {
        resetStoreOnLogout();
        resetSsoLogin();
        navigate('/login');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate, resetStoreOnLogout, resetSsoLogin]);

  /**
   * Connect EventHub when user is logged in (with srToken). Disconnect when logged out.
   */
  useEffect(() => {
    if (isLoggedIn && authenticatedUser?.srToken) {
      webSocket.connect(undefined, authenticatedUser.srToken);
    } else {
      webSocket.disconnect();
    }
  }, [isLoggedIn, authenticatedUser]);

  /**
   * Controls the 2FA activation overlay once user data is available.
   * Show only when:
   * - user is authenticated
   * - role is SuperAdmin, Admin, or Contributor (Reader / External never see the overlay)
   * - user has not enabled TOTP
   * - login is not via SSO
   *
   * When the company's `TOTP_REQUIRED` policy grace period has elapsed (per `GET /user/totp/status`),
   * the overlay opens in **mandatory** mode regardless of remind/skip preferences. Otherwise it
   * follows the existing promo rules (permanent opt-out, remind-later timestamp).
   */
  const authenticatedUserId = authenticatedUser?.id;
  const authenticatedUserRole = authenticatedUser?.role;
  const hasTotpEnabled = authenticatedUser?.isTotp === true;
  useEffect(() => {
    if (isActivateTotpPromoSuppressedPath(listPathname)) {
      closeActivateTotp();
      return;
    }

    if (isLoadingAuthenticatedUser || !isLoggedIn || !authenticatedUserId) return;

    if (!authenticatedUserRole || !isTotpPromoEligibleRole(authenticatedUserRole)) {
      closeActivateTotp();
      return;
    }

    if (hasTotpEnabled || isSsoLogin) {
      closeActivateTotp();
      return;
    }

    const openPromoOrClose = (remindDays = 7) => {
      const isRemindExpired = activateTotpRemindAt === null || Date.now() >= activateTotpRemindAt;
      if (!activateTotpNeverShowAgain && isRemindExpired) {
        openActivateTotp({ remindDays });
      } else {
        closeActivateTotp();
      }
    };

    let cancelled = false;
    userService
      .totpStatus()
      .then((status) => {
        if (cancelled) return;
        if (isTotpEnrollmentMandatory(status)) {
          openActivateTotp({ isMandatory: true });
        } else {
          openPromoOrClose(getActivateTotpRemindDays(status.gracePeriodEnds));
        }
      })
      .catch(() => {
        // Status fetch failed (e.g. transient network error): fall back to the existing promo rules.
        if (!cancelled) openPromoOrClose();
      });

    return () => {
      cancelled = true;
    };
  }, [
    isLoadingAuthenticatedUser,
    isLoggedIn,
    authenticatedUserId,
    authenticatedUserRole,
    hasTotpEnabled,
    activateTotpRemindAt,
    activateTotpNeverShowAgain,
    isSsoLogin,
    listPathname,
    openActivateTotp,
    closeActivateTotp,
  ]);

  /**
   * Prevents UI noise: swallows handled global unhandledrejection events
   * that have __handledByInterceptor (e.g., Axios network errors).
   * Must run in useEffect with cleanup so we don't add a new listener on every render (would leak and crash).
   */
  useEffect(() => {
    const handler = (evt: PromiseRejectionEvent) => {
      const reason: any = evt.reason;
      if (reason && reason.__handledByInterceptor) {
        evt.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  /**
   * App-updated overlay: poll /version.json and open overlay when server build differs from client.
   * Skips in dev/localhost. Runs on interval and when tab becomes visible.
   */
  useEffect(() => {
    if (import.meta.env.DEV || window.location.hostname === 'localhost') return;
    const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const check = async (): Promise<void> => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (data.buildId != null && data.buildId !== __BUILD_ID__) {
          useAppStore.getState().appEvents.appUpdated.open();
          if (intervalId != null) clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        // Ignore network errors
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void check();
    };

    document.addEventListener('visibilitychange', onVisibility);
    void check();
    intervalId = setInterval(() => void check(), CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalId != null) clearInterval(intervalId);
    };
  }, []);

  /**
   * Shows a loading overlay while authentication is initializing.
   * Once ready, renders the app's children inside the AuthContext provider.
   */
  return (
    <AuthContext.Provider value={contextValue}>
      <LoadingOverlay label={t('common.loading')} isVisible={isLoadingAuthenticatedUser} />
      {/* Children components are only rendered after loading finishes */}
      {!isLoadingAuthenticatedUser && <>{children}</>}
    </AuthContext.Provider>
  );
};
